import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clinicAPI, postsAPI } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { usePostModal } from '../../contexts/PostModalContext';
import ChannelPostReactions from '../../components/channels/ChannelPostReactions';
import '../../components/channels/ChannelPostReactions.css';
import PostReactionsModal from '../../components/social/posts/PostReactionsModal';
import { resolveImageUrl, withPlaceholder, getUserPlaceholderImage } from '../../utils/imageUtils';
import { getUserTypeColor } from '../../utils/userTypeUtils';
import { showErrorToast, showSuccessToast } from '../../utils/notificationUtils';
import './ClinicPage.css';

// Backend weekday name -> JS Date day index (Sunday = 0)
const WEEKDAY_INDEX = {
    sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
    thursday: 4, friday: 5, saturday: 6,
};

const ClinicPage = () => {
    const { clinicId } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { user, getUserType } = useAuth();
    // Page chrome follows the logged-in user's user-type color (like the rest of the app)
    const userTypeColor = getUserTypeColor(getUserType());
    const pageStyle = { '--user-type-color': userTypeColor };

    const [clinic, setClinic] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notAvailable, setNotAvailable] = useState(false);
    const [activeTab, setActiveTab] = useState('posts');
    const [isFollowing, setIsFollowing] = useState(false);
    const [followersCount, setFollowersCount] = useState(0);

    // Compact sticky bar shown once the big header scrolls out of view
    const headerRef = useRef(null);
    const [showStickyBar, setShowStickyBar] = useState(false);

    useEffect(() => {
        const target = headerRef.current;
        if (!target) return;
        const observer = new IntersectionObserver(
            ([entry]) => setShowStickyBar(!entry.isIntersecting),
            { rootMargin: '-70px 0px 0px 0px' }
        );
        observer.observe(target);
        return () => observer.disconnect();
    }, [clinic]);

    const fetchClinic = async () => {
        setLoading(true);
        try {
            const resp = await clinicAPI.getClinicById(clinicId);
            const data = resp?.data?.data;
            if (data) {
                setClinic(data);
                setIsFollowing(!!data?.is_following);
                setFollowersCount(data?.followers_count || 0);
                const isOwner = !!data?.clinic?.is_owner;
                const isMember = !!data?.clinic?.is_member;
                if (data.status !== 'verified' && !isOwner && !isMember) {
                    setNotAvailable(true);
                }
            } else {
                setNotAvailable(true);
            }
        } catch {
            setNotAvailable(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (clinicId) fetchClinic();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clinicId]);

    const isOwner = !!clinic?.clinic?.is_owner;
    const isMember = !!clinic?.clinic?.is_member;
    const canBook = user?.user_type === 'PET_GUARDIAN' || user?.user_type === 'SERVICE_PROVIDER';

    const localized = (en, ar) => (i18n.language?.startsWith('ar') ? (ar || en) : (en || ar));

    const handleToggleFollow = async () => {
        try {
            if (isFollowing) {
                const resp = await clinicAPI.unfollowClinic(clinic.id);
                setIsFollowing(false);
                setFollowersCount(resp?.data?.data?.followers_count ?? Math.max(0, followersCount - 1));
            } else {
                const resp = await clinicAPI.followClinic(clinic.id);
                setIsFollowing(true);
                setFollowersCount(resp?.data?.data?.followers_count ?? followersCount + 1);
                showSuccessToast(t('clinicPage.followedToast', { defaultValue: 'You are now following this clinic' }));
            }
        } catch {
            showErrorToast(t('clinicPage.followFailed', { defaultValue: 'Could not update follow status. Please try again.' }));
        }
    };

    const teamMembers = useMemo(() => {
        const members = (clinic?.clinic_members || []).filter(m => m.status === 'accepted');
        // Surface the owner first
        return [...members].sort((a, b) => {
            const aOwner = a.doctor_id === clinic?.owner_id ? 0 : 1;
            const bOwner = b.doctor_id === clinic?.owner_id ? 0 : 1;
            return aOwner - bOwner;
        });
    }, [clinic]);

    const workingHours = useMemo(() => {
        const hours = clinic?.working_hours || [];
        // Order the week starting from today so the current day is on top
        const todayIdx = new Date().getDay();
        return [...hours].sort((a, b) => {
            const ai = WEEKDAY_INDEX[a.day] ?? 7;
            const bi = WEEKDAY_INDEX[b.day] ?? 7;
            return ((ai - todayIdx + 7) % 7) - ((bi - todayIdx + 7) % 7);
        });
    }, [clinic]);

    const weekdayName = (day) => {
        const idx = WEEKDAY_INDEX[day];
        if (idx === undefined) return day;
        return new Intl.DateTimeFormat(i18n.language || 'en', { weekday: 'long' })
            .format(new Date(2024, 0, idx + 7)); // 2024-01-07 was a Sunday
    };

    const bookUrl = user?.user_type === 'SERVICE_PROVIDER'
        ? `/medical/dashboard/petshopowner/appointment-request?clinicId=${clinic?.id}`
        : `/medical/dashboard/petguardian/appointment-request?clinicId=${clinic?.id}`;

    if (loading) {
        return (
            <div className="clinic-page" style={pageStyle}>
                <div className="clinic-page-layout">
                    {/* Left rail skeleton */}
                    <aside className="clinic-rail clinic-rail-left">
                        <div className="clinic-card clinic-rail-card">
                            <div className="clinic-skel clinic-skel-title" />
                            <div className="clinic-skel clinic-skel-line" />
                            <div className="clinic-skel clinic-skel-line clinic-skel-w70" />
                            <div className="clinic-skel clinic-skel-line clinic-skel-w85" />
                            <div className="clinic-skel clinic-skel-line clinic-skel-w60" />
                        </div>
                    </aside>

                    {/* Center skeleton */}
                    <div className="clinic-page-inner">
                        <div className="clinic-skel clinic-skel-cover" />
                        <div className="clinic-header-card">
                            <div className="clinic-header-main">
                                <div className="clinic-skel clinic-skel-logo" />
                                <div className="clinic-skel-header-lines">
                                    <div className="clinic-skel clinic-skel-line clinic-skel-w40 clinic-skel-lg" />
                                    <div className="clinic-skel clinic-skel-line clinic-skel-w25" />
                                    <div className="clinic-skel clinic-skel-line clinic-skel-w30" />
                                </div>
                            </div>
                            <div className="clinic-skel clinic-skel-actions" />
                        </div>
                        <div className="clinic-skel clinic-skel-tabs" />
                        <div className="clinic-skel clinic-skel-post" />
                        <div className="clinic-skel clinic-skel-post" />
                    </div>

                    {/* Right rail skeleton */}
                    <aside className="clinic-rail clinic-rail-right">
                        <div className="clinic-card clinic-rail-card">
                            <div className="clinic-skel clinic-skel-title clinic-skel-w50" />
                            <div className="clinic-skel clinic-skel-line" />
                            <div className="clinic-skel clinic-skel-line clinic-skel-w80" />
                            <div className="clinic-skel clinic-skel-line" />
                            <div className="clinic-skel clinic-skel-line clinic-skel-w70" />
                        </div>
                    </aside>
                </div>
            </div>
        );
    }

    if (notAvailable || !clinic) {
        return (
            <div className="clinic-page" style={pageStyle}>
                <div className="clinic-page-unavailable card">
                    <i className="fas fa-hospital clinic-unavailable-icon"></i>
                    <h2>{t('clinicPage.unavailableTitle', { defaultValue: 'This clinic page is not available' })}</h2>
                    <p>{t('clinicPage.unavailableBody', { defaultValue: 'The clinic you are looking for does not exist or has not been approved yet.' })}</p>
                </div>
            </div>
        );
    }

    const rating = parseFloat(clinic.average_rating || 0);
    const logoSrc = resolveImageUrl(clinic.logo);
    const coverSrc = resolveImageUrl(clinic.cover_image);

    return (
        <div className="clinic-page" style={pageStyle}>
            {/* Compact sticky bar once the big header is scrolled out of view */}
            {showStickyBar && (
                <div className="clinic-sticky-bar">
                    <div className="clinic-sticky-bar-inner">
                        <div className="clinic-sticky-identity">
                            <img
                                src={withPlaceholder(logoSrc, getUserPlaceholderImage())}
                                alt={clinic.name_en}
                                className="clinic-sticky-logo"
                            />
                            <span className="clinic-sticky-name">
                                {localized(clinic.name_en, clinic.name_ar)}
                                {clinic.status === 'verified' && (
                                    <i className="fas fa-check-circle clinic-verified-badge"></i>
                                )}
                            </span>
                        </div>
                        <div className="clinic-sticky-actions">
                            {isOwner && (
                                <EditPageButton
                                    clinic={clinic}
                                    onSaved={(updated) => setClinic(prev => ({ ...prev, ...updated }))}
                                />
                            )}
                            {!isOwner && (
                                <button
                                    className={`clinic-btn clinic-btn-small ${isFollowing ? 'clinic-btn-following' : 'clinic-btn-follow'}`}
                                    onClick={handleToggleFollow}
                                >
                                    <i className={`fas ${isFollowing ? 'fa-check' : 'fa-plus'}`}></i>
                                    {isFollowing
                                        ? t('clinicPage.following', { defaultValue: 'Following' })
                                        : t('clinicPage.follow', { defaultValue: 'Follow' })}
                                </button>
                            )}
                            {canBook && (
                                <button
                                    className="clinic-btn clinic-btn-primary clinic-btn-small"
                                    onClick={() => navigate(bookUrl)}
                                >
                                    <i className="fas fa-calendar-check"></i> {t('clinicPage.bookAppointment', { defaultValue: 'Book Appointment' })}
                                </button>
                            )}
                            {!isOwner && clinic.owner?.id && (
                                <button
                                    className="clinic-btn clinic-btn-secondary clinic-btn-small"
                                    onClick={() => navigate(`/chat/${clinic.owner.id}/social`)}
                                >
                                    <i className="fas fa-comment-dots"></i> {t('clinicPage.message', { defaultValue: 'Message' })}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="clinic-page-layout">
                {/* Left rail — About (sticky, fills the empty left side) */}
                <aside className="clinic-rail clinic-rail-left">
                    <AboutCard clinic={clinic} isOwner={isOwner} />
                </aside>

                <div className="clinic-page-inner">
                {/* Cover */}
                <div
                    className="clinic-cover"
                    style={coverSrc ? { backgroundImage: `url(${coverSrc})` } : undefined}
                >
                    {!coverSrc && <i className="fas fa-paw clinic-cover-paw"></i>}
                </div>

                {/* Header */}
                <div className="clinic-header-card" ref={headerRef}>
                    <div className="clinic-header-main">
                        <div className="clinic-logo-wrap">
                            <img
                                src={withPlaceholder(logoSrc, getUserPlaceholderImage())}
                                alt={clinic.name_en}
                                className="clinic-logo"
                            />
                        </div>
                        <div className="clinic-identity">
                            <h1 className="clinic-name">
                                {localized(clinic.name_en, clinic.name_ar)}
                                {clinic.status === 'verified' && (
                                    <i className="fas fa-check-circle clinic-verified-badge" title={t('clinicPage.verified', { defaultValue: 'Verified clinic' })}></i>
                                )}
                            </h1>
                            <div className="clinic-subline">
                                {clinic.clinic_type && (
                                    <span className="clinic-type-chip">{localized(clinic.clinic_type.name_en, clinic.clinic_type.name_ar)}</span>
                                )}
                                {clinic.city?.name_en && (
                                    <span className="clinic-location-line">
                                        <i className="fas fa-map-marker-alt"></i> {localized(clinic.city.name_en, clinic.city.name_ar)}{clinic.country?.name_en ? `, ${localized(clinic.country.name_en, clinic.country.name_ar)}` : ''}
                                    </span>
                                )}
                            </div>
                            <div className="clinic-rating-line">
                                <span className="clinic-stars">
                                    {[1, 2, 3, 4, 5].map(star => (
                                        <i
                                            key={star}
                                            className={`${rating >= star - 0.5 ? 'fas' : 'far'} fa-star`}
                                            style={{ color: rating >= star - 0.5 ? '#F59E0B' : '#D1D5DB' }}
                                        ></i>
                                    ))}
                                </span>
                                <span className="clinic-rating-text">
                                    {rating > 0
                                        ? t('clinicPage.ratingValue', { rating: rating.toFixed(1), count: clinic.ratings_count || 0, defaultValue: '{{rating}} ({{count}} reviews)' })
                                        : t('clinicPage.noRatings', { defaultValue: 'No reviews yet' })}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="clinic-header-actions">
                        {isOwner && (
                            <>
                                <button
                                    className="clinic-btn clinic-btn-secondary"
                                    onClick={() => navigate(`/medical/clinic/${clinic.id}/setup`)}
                                >
                                    <i className="fas fa-cog"></i> {t('clinicPage.manageClinic', { defaultValue: 'Manage Clinic' })}
                                </button>
                                <EditPageButton
                                    clinic={clinic}
                                    onSaved={(updated) => setClinic(prev => ({ ...prev, ...updated }))}
                                />
                            </>
                        )}
                        {!isOwner && (
                            <button
                                className={`clinic-btn ${isFollowing ? 'clinic-btn-following' : 'clinic-btn-follow'}`}
                                onClick={handleToggleFollow}
                            >
                                <i className={`fas ${isFollowing ? 'fa-check' : 'fa-plus'}`}></i>
                                {isFollowing
                                    ? t('clinicPage.following', { defaultValue: 'Following' })
                                    : t('clinicPage.follow', { defaultValue: 'Follow' })}
                                {followersCount > 0 && <span className="clinic-followers-count">· {followersCount}</span>}
                            </button>
                        )}
                        {canBook && (
                            <button
                                className="clinic-btn clinic-btn-primary"
                                onClick={() => navigate(bookUrl)}
                            >
                                <i className="fas fa-calendar-check"></i> {t('clinicPage.bookAppointment', { defaultValue: 'Book Appointment' })}
                            </button>
                        )}
                        {!isOwner && clinic.owner?.id && (
                            <button
                                className="clinic-btn clinic-btn-secondary"
                                onClick={() => navigate(`/chat/${clinic.owner.id}/social`)}
                            >
                                <i className="fas fa-comment-dots"></i> {t('clinicPage.message', { defaultValue: 'Message' })}
                            </button>
                        )}
                        {isMember && !isOwner && (
                            <span className="clinic-member-note">
                                <i className="fas fa-user-md"></i> {t('clinicPage.youAreTeamMember', { defaultValue: 'You are a team member' })}
                            </span>
                        )}
                    </div>
                </div>

                {/* Tabs */}
                <div className="clinic-tabs" role="tablist">
                    {['posts', 'team', 'services'].map(tabKey => (
                        <button
                            key={tabKey}
                            role="tab"
                            aria-selected={activeTab === tabKey}
                            className={`clinic-tab ${activeTab === tabKey ? 'active' : ''}`}
                            onClick={() => setActiveTab(tabKey)}
                        >
                            {t(`clinicPage.tabs.${tabKey}`, { defaultValue: tabKey.charAt(0).toUpperCase() + tabKey.slice(1) })}
                        </button>
                    ))}
                </div>

                {/* Tab bodies */}
                <div className="clinic-tab-body">
                    {activeTab === 'posts' && (
                        <ClinicPostsSection clinic={clinic} isOwner={isOwner} localized={localized} />
                    )}

                    {activeTab === 'team' && (
                        <div className="clinic-card">
                            <h3 className="clinic-card-title">
                                <i className="fas fa-user-md"></i> {t('clinicPage.teamTitle', { defaultValue: 'Our Team' })}
                                <span className="clinic-card-count">{teamMembers.length}</span>
                            </h3>
                            {teamMembers.length > 0 ? (
                                <div className="clinic-team-grid">
                                    {teamMembers.map(member => {
                                        const isTeamOwner = member.doctor_id === clinic.owner_id;
                                        const roleLabel = isTeamOwner
                                            ? t('clinicPage.roleOwner', { defaultValue: 'Owner' })
                                            : member.role === 'clinic_moderator'
                                                ? t('clinicPage.roleModerator', { defaultValue: 'Clinic Moderator' })
                                                : t('clinicPage.roleDoctor', { defaultValue: 'Veterinarian' });
                                        return (
                                            <div className="clinic-team-card" key={member.doctor_id}>
                                                <img
                                                    src={withPlaceholder(resolveImageUrl(member.profile_image), getUserPlaceholderImage())}
                                                    alt={member.doctor_name}
                                                    className="clinic-team-avatar"
                                                    onClick={() => navigate(`/social/profile/${member.doctor_id}`)}
                                                />
                                                <div className="clinic-team-info">
                                                    <span
                                                        className="clinic-team-name"
                                                        onClick={() => navigate(`/social/profile/${member.doctor_id}`)}
                                                    >
                                                        {member.doctor_name}
                                                    </span>
                                                    <span className={`clinic-team-role ${isTeamOwner ? 'owner' : ''}`}>{roleLabel}</span>
                                                </div>
                                                {user?.id !== member.doctor_id && member.role !== 'clinic_moderator' && (
                                                    <button
                                                        className="clinic-btn clinic-btn-small clinic-btn-secondary"
                                                        onClick={() => navigate(`/chat/${member.doctor_id}/social`)}
                                                    >
                                                        <i className="fas fa-comment-dots"></i> {t('clinicPage.message', { defaultValue: 'Message' })}
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="clinic-about-empty">{t('clinicPage.noTeam', { defaultValue: 'No team members to show yet.' })}</p>
                            )}
                        </div>
                    )}

                    {activeTab === 'services' && (
                        <div className="clinic-card">
                            <h3 className="clinic-card-title">
                                <i className="fas fa-stethoscope"></i> {t('clinicPage.servicesTitle', { defaultValue: 'Specialties & Services' })}
                                <span className="clinic-card-count">{(clinic.service_lists || []).length}</span>
                            </h3>
                            {(clinic.service_lists || []).length > 0 ? (
                                <div className="clinic-services-grid">
                                    {clinic.service_lists.map(service => (
                                        <div className="clinic-service-item" key={service.id}>
                                            <div className="clinic-service-icon">
                                                <i className="fas fa-paw"></i>
                                            </div>
                                            <div className="clinic-service-text">
                                                <span className="clinic-service-name">{localized(service.name_en, service.name_ar)}</span>
                                                {localized(service.description_en, service.description_ar) && (
                                                    <span className="clinic-service-desc">{localized(service.description_en, service.description_ar)}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="clinic-about-empty">{t('clinicPage.noServices', { defaultValue: 'No specialties listed yet.' })}</p>
                            )}
                        </div>
                    )}
                </div>
                </div>

                {/* Right rail — Working Hours (sticky, fills the empty right side) */}
                <aside className="clinic-rail clinic-rail-right">
                    <WorkingHoursCard workingHours={workingHours} weekdayName={weekdayName} />
                </aside>
            </div>
        </div>
    );
};

/**
 * About card (left rail) — description + contact/location info.
 */
const AboutCard = ({ clinic, isOwner }) => {
    const { t } = useTranslation();

    return (
        <div className="clinic-card clinic-rail-card">
            <h3 className="clinic-card-title">
                <i className="fas fa-info-circle"></i> {t('clinicPage.aboutTitle', { defaultValue: 'About' })}
            </h3>
            {clinic.about ? (
                <p className="clinic-about-text">{clinic.about}</p>
            ) : (
                <p className="clinic-about-empty">
                    {isOwner
                        ? t('clinicPage.aboutEmptyOwner', { defaultValue: 'Tell visitors about your clinic — open Edit Page to add a description.' })
                        : t('clinicPage.aboutEmpty', { defaultValue: 'This clinic has not added a description yet.' })}
                </p>
            )}
            <ul className="clinic-info-list">
                {clinic.full_address && (
                    <li>
                        <i className="fas fa-map-marker-alt"></i>
                        <span>{clinic.full_address}</span>
                    </li>
                )}
                {clinic.telephone && (
                    <li>
                        <i className="fas fa-phone"></i>
                        <span dir="ltr">{clinic.telephone}</span>
                    </li>
                )}
                {clinic.mobile && (
                    <li>
                        <i className="fas fa-mobile-alt"></i>
                        <span dir="ltr">{clinic.mobile}</span>
                    </li>
                )}
                {clinic.email && (
                    <li>
                        <i className="fas fa-envelope"></i>
                        <span>{clinic.email}</span>
                    </li>
                )}
                {clinic.website && (
                    <li>
                        <i className="fas fa-globe"></i>
                        <a href={/^https?:\/\//i.test(clinic.website) ? clinic.website : `https://${clinic.website}`} target="_blank" rel="noopener noreferrer">
                            {clinic.website}
                        </a>
                    </li>
                )}
                {clinic.establishment_date && (
                    <li>
                        <i className="fas fa-calendar-day"></i>
                        <span>{t('clinicPage.established', { date: clinic.establishment_date, defaultValue: 'Established {{date}}' })}</span>
                    </li>
                )}
            </ul>
        </div>
    );
};

/**
 * Working hours card (right rail) — today's hours highlighted, week ordered
 * starting from today.
 */
const WorkingHoursCard = ({ workingHours, weekdayName }) => {
    const { t } = useTranslation();

    return (
        <div className="clinic-card clinic-rail-card">
            <h3 className="clinic-card-title">
                <i className="fas fa-clock"></i> {t('clinicPage.workingHours', { defaultValue: 'Working Hours' })}
            </h3>
            {workingHours.length > 0 ? (
                <ul className="clinic-hours-list">
                    {workingHours.map(wh => {
                        const isToday = WEEKDAY_INDEX[wh.day] === new Date().getDay();
                        const isClosed = !wh.opening_time || !wh.closing_time || wh.opening_time === wh.closing_time;
                        return (
                            <li key={wh.day} className={isToday ? 'today' : ''}>
                                <span className="clinic-hours-day">{weekdayName(wh.day)}</span>
                                <span className={`clinic-hours-time ${isClosed ? 'closed' : ''}`}>
                                    {isClosed
                                        ? t('clinicPage.closed', { defaultValue: 'Closed' })
                                        : `${wh.opening_time} - ${wh.closing_time}`}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <p className="clinic-about-empty">{t('clinicPage.noWorkingHours', { defaultValue: 'Working hours not provided.' })}</p>
            )}
        </div>
    );
};

/**
 * Clinic page posts: owner composer ("post as clinic") + the clinic's posts.
 * Posts open in the shared post modal so reactions/comments work as usual.
 */
const ClinicPostsSection = ({ clinic, isOwner, localized }) => {
    const { t } = useTranslation();
    const { user, getUserType } = useAuth();
    const userTypeColor = getUserTypeColor(getUserType());
    const { openPostModal } = usePostModal();
    const [posts, setPosts] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    // Reactions modal (who reacted) + per-post API update patches
    const [reactionsModal, setReactionsModal] = useState({ open: false, postId: null });
    const [postPatches, setPostPatches] = useState({});

    // Composer state (owner only)
    const [content, setContent] = useState('');
    const [files, setFiles] = useState([]);
    const [previews, setPreviews] = useState([]);
    const [publishing, setPublishing] = useState(false);
    const fileInputRef = useRef(null);

    // Local like state per post id: { liked, count } (initial values from API,
    // then driven by ChannelPostReactions updates)
    const [likeState, setLikeState] = useState({});

    const fetchPosts = useCallback(async (page = 1) => {
        try {
            const resp = await clinicAPI.getClinicPosts(clinic.id, { page, per_page: 10 });
            const list = resp?.data?.data || [];
            setPosts(prev => (page === 1 ? list : [...prev, ...list]));
            setPagination(resp?.data?.pagination || null);
        } catch {
            // leave previous state untouched
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [clinic.id]);

    useEffect(() => {
        fetchPosts(1);
    }, [fetchPosts]);

    useEffect(() => {
        const urls = files.map(f => URL.createObjectURL(f));
        setPreviews(urls);
        return () => urls.forEach(u => URL.revokeObjectURL(u));
    }, [files]);

    const handlePublish = async () => {
        const trimmed = content.trim();
        if (!trimmed && files.length === 0) return;
        setPublishing(true);
        try {
            const resp = await postsAPI.createPost({
                content: trimmed,
                clinic_id: clinic.id,
                files: files.length > 0 ? files : undefined,
                privacy: 'everyone',
            });
            const created = resp?.data?.data;
            if (created) {
                setPosts(prev => [created, ...prev]);
                setContent('');
                setFiles([]);
                showSuccessToast(t('clinicPage.postPublished', { defaultValue: 'Post published' }));
            } else {
                showErrorToast(t('clinicPage.postFailed', { defaultValue: 'Could not publish the post. Please try again.' }));
            }
        } catch {
            showErrorToast(t('clinicPage.postFailed', { defaultValue: 'Could not publish the post. Please try again.' }));
        } finally {
            setPublishing(false);
        }
    };

    const loadMore = () => {
        if (!pagination?.has_more_pages || loadingMore) return;
        setLoadingMore(true);
        fetchPosts(pagination.current_page + 1);
    };

    const getLikeState = (post) => likeState[post.id] ?? {
        liked: !!(post.is_liked || post.user_reaction_type),
        count: post.reactions_count || 0,
    };

    const openComments = (post) => {
        if (post.id) openPostModal(post.id);
    };

    const clinicName = localized(clinic.name_en, clinic.name_ar);
    const clinicLogo = resolveImageUrl(clinic.logo);
    const timeAgo = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
        if (seconds < 60) return t('clinicPage.justNow', { defaultValue: 'Just now' });
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return t('clinicPage.minutesAgo', { count: minutes, defaultValue: '{{count}}m' });
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return t('clinicPage.hoursAgo', { count: hours, defaultValue: '{{count}}h' });
        const days = Math.floor(hours / 24);
        if (days < 7) return t('clinicPage.daysAgo', { count: days, defaultValue: '{{count}}d' });
        return date.toLocaleDateString();
    };

    return (
        <div>
            {isOwner && (
                <div className="clinic-composer">
                    <div className="clinic-composer-top">
                        <img
                            src={withPlaceholder(clinicLogo, getUserPlaceholderImage())}
                            alt={clinicName}
                            className="clinic-composer-avatar"
                        />
                        <textarea
                            className="clinic-composer-input"
                            rows={3}
                            value={content}
                            maxLength={10000}
                            onChange={e => setContent(e.target.value)}
                            placeholder={t('clinicPage.composerPlaceholder', { name: clinicName, defaultValue: 'Post an update as {{name}}...' })}
                        />
                    </div>

                    {previews.length > 0 && (
                        <div className="clinic-composer-previews">
                            {previews.map((url, idx) => (
                                <div className="clinic-composer-preview" key={url}>
                                    <img src={url} alt="" />
                                    <button
                                        type="button"
                                        className="clinic-composer-preview-remove"
                                        onClick={() => setFiles(prev => prev.filter((_, i) => i !== idx))}
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="clinic-composer-actions">
                        <div className="clinic-composer-tools">
                            <button type="button" className="clinic-composer-tool" onClick={() => fileInputRef.current?.click()}>
                                <i className="fas fa-image" style={{ color: userTypeColor }}></i>
                                {t('clinicPage.addPhotos', { defaultValue: 'Photos/Videos' })}
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*,video/*"
                                multiple
                                hidden
                                onChange={e => {
                                    const selected = Array.from(e.target.files || []);
                                    setFiles(prev => [...prev, ...selected].slice(0, 10));
                                    e.target.value = '';
                                }}
                            />
                        </div>
                        <button
                            type="button"
                            className="clinic-btn clinic-btn-primary clinic-btn-small"
                            onClick={handlePublish}
                            disabled={publishing || (!content.trim() && files.length === 0)}
                        >
                            {publishing
                                ? t('clinicPage.publishing', { defaultValue: 'Publishing...' })
                                : t('clinicPage.publish', { defaultValue: 'Publish' })}
                        </button>
                    </div>
                </div>
            )}

            {loading ? (
                [1, 2, 3].map(i => (
                    <div key={`post-skel-${i}`} className="clinic-post-card clinic-post-skel" aria-hidden="true">
                        <div className="clinic-post-header">
                            <div className="clinic-skel clinic-skel-avatar" />
                            <div className="clinic-post-header-text">
                                <div className="clinic-skel clinic-skel-line clinic-skel-w40" style={{ marginBottom: 6 }} />
                                <div className="clinic-skel clinic-skel-line clinic-skel-w25" />
                            </div>
                        </div>
                        <div className="clinic-skel clinic-skel-line" style={{ marginTop: 12 }} />
                        <div className="clinic-skel clinic-skel-line clinic-skel-w80" />
                        <div className="clinic-skel clinic-skel-media" />
                    </div>
                ))
            ) : posts.length === 0 ? (
                <div className="clinic-posts-empty">
                    <i className="fas fa-comment-dots"></i>
                    {isOwner
                        ? t('clinicPage.noPostsOwner', { defaultValue: 'No posts yet — share your first update with followers.' })
                        : t('clinicPage.noPosts', { defaultValue: 'No posts yet.' })}
                </div>
            ) : (
                posts.map(post => {
                    const patched = postPatches[post.id] ? { ...post, ...postPatches[post.id] } : post;
                    const media = (patched.media || []).filter(m => m && (m.file_type || '').startsWith('image'));
                    const like = getLikeState(patched);
                    return (
                        <article
                            key={post.id}
                            className="clinic-post-card"
                            onClick={() => post.id && openPostModal(post.id)}
                        >
                            <div className="clinic-post-header">
                                <img
                                    src={withPlaceholder(clinicLogo, getUserPlaceholderImage())}
                                    alt={clinicName}
                                    className="clinic-post-avatar"
                                />
                                <div className="clinic-post-header-text">
                                    <span className="clinic-post-author">
                                        {clinicName}
                                        <i className="fas fa-check-circle" style={{ color: '#00D4B3', fontSize: '13px' }}></i>
                                    </span>
                                    <span className="clinic-post-meta">
                                        {timeAgo(patched.published_at || patched.created_at)}
                                    </span>
                                </div>
                            </div>
                            {patched.content && <p className="clinic-post-content">{patched.content}</p>}
                            {media.length > 0 && (
                                <div className={`clinic-post-media-grid count-${Math.min(media.length, 4)}`}>
                                    {media.slice(0, 4).map(m => (
                                        <img key={m.id} src={m.url} alt="" loading="lazy" onClick={e => e.stopPropagation()} />
                                    ))}
                                </div>
                            )}
                            <div className="clinic-post-actions" onClick={(e) => e.stopPropagation()}>
                                <ChannelPostReactions
                                    post={{
                                        ...patched,
                                        likes: like.count,
                                        shares: patched.shares_count ?? 0,
                                        comments: patched.comments_count ?? 0,
                                        reposts_count: patched.reposts_count ?? 0,
                                        user_has_reposted: patched.user_has_reposted ?? false,
                                        user_reaction_type: like.liked ? (patched.user_reaction_type || 'like') : (patched.user_reaction_type || null),
                                        reaction_counts: patched.reaction_counts || {},
                                        reactions_count: like.count,
                                    }}
                                    onLike={(updatedPost) => {
                                        setPostPatches(prev => ({
                                            ...prev,
                                            [post.id]: { ...(prev[post.id] || {}), ...updatedPost },
                                        }));
                                        setLikeState(prev => ({
                                            ...prev,
                                            [post.id]: {
                                                liked: !!updatedPost.user_reaction_type,
                                                count: updatedPost.reactions_count ?? updatedPost.likes ?? like.count,
                                            },
                                        }));
                                    }}
                                    onComment={() => openComments(post)}
                                    onShowReactions={() => setReactionsModal({ open: true, postId: post.id })}
                                />
                            </div>
                        </article>
                    );
                })
            )}

            {!loading && pagination?.has_more_pages ? (
                <button type="button" className="clinic-load-more" onClick={loadMore} disabled={loadingMore}>
                    {loadingMore
                        ? t('clinicPage.loadingMore', { defaultValue: 'Loading...' })
                        : t('clinicPage.loadMore', { defaultValue: 'Load more' })}
                </button>
            ) : null}

            <PostReactionsModal
                isOpen={reactionsModal.open}
                onClose={() => setReactionsModal({ open: false, postId: null })}
                postId={reactionsModal.postId}
                currentUser={user}
            />
        </div>
    );
};

/**
 * Owner-only "Edit Page" button + modal (cover image + about text).
 * Everything else is edited through the clinic setup page.
 */
const EditPageButton = ({ clinic, onSaved }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [about, setAbout] = useState(clinic.about || '');
    const [coverFile, setCoverFile] = useState(null);
    const [coverPreview, setCoverPreview] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setAbout(clinic.about || '');
    }, [clinic.about]);

    useEffect(() => {
        if (!coverFile) {
            setCoverPreview(null);
            return;
        }
        const url = URL.createObjectURL(coverFile);
        setCoverPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [coverFile]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('about', about);
            if (coverFile) {
                formData.append('cover_image', coverFile);
            }
            const resp = await clinicAPI.updateClinic(clinic.id, formData);
            const updated = resp?.data?.data;
            if (updated) {
                onSaved(updated);
                showSuccessToast(t('clinicPage.editSaved', { defaultValue: 'Page updated successfully' }));
                setOpen(false);
                setCoverFile(null);
            } else {
                showErrorToast(t('clinicPage.editFailed', { defaultValue: 'Could not update the page. Please try again.' }));
            }
        } catch {
            showErrorToast(t('clinicPage.editFailed', { defaultValue: 'Could not update the page. Please try again.' }));
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <button className="clinic-btn clinic-btn-primary" onClick={() => setOpen(true)}>
                <i className="fas fa-edit"></i> {t('clinicPage.editPage', { defaultValue: 'Edit Page' })}
            </button>
            {open && (
                <div className="clinic-edit-overlay" onClick={() => setOpen(false)}>
                    <div className="clinic-edit-modal" onClick={e => e.stopPropagation()}>
                        <div className="clinic-edit-modal-header">
                            <h3>{t('clinicPage.editTitle', { defaultValue: 'Edit Clinic Page' })}</h3>
                            <button className="clinic-edit-close" onClick={() => setOpen(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <div className="clinic-edit-cover-preview">
                            {coverPreview || clinic.cover_image ? (
                                <img src={coverPreview || clinic.cover_image} alt="cover" />
                            ) : (
                                <div className="clinic-edit-cover-placeholder">
                                    <i className="fas fa-image"></i>
                                </div>
                            )}
                            <label className="clinic-edit-cover-upload">
                                <i className="fas fa-camera"></i> {t('clinicPage.changeCover', { defaultValue: 'Change cover photo' })}
                                <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={e => setCoverFile(e.target.files?.[0] || null)}
                                />
                            </label>
                        </div>

                        <label className="clinic-edit-label">{t('clinicPage.aboutLabel', { defaultValue: 'About' })}</label>
                        <textarea
                            className="clinic-edit-textarea"
                            rows={5}
                            maxLength={2000}
                            value={about}
                            onChange={e => setAbout(e.target.value)}
                            placeholder={t('clinicPage.aboutPlaceholder', { defaultValue: 'Describe your clinic, your team, and what makes you special...' })}
                        />
                        <span className="clinic-edit-charcount">{about.length}/2000</span>

                        <div className="clinic-edit-actions">
                            <button className="clinic-btn clinic-btn-secondary" onClick={() => setOpen(false)}>
                                {t('common.cancel', { defaultValue: 'Cancel' })}
                            </button>
                            <button
                                className="clinic-btn clinic-btn-primary"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? t('common.saving', { defaultValue: 'Saving...' }) : t('common.save', { defaultValue: 'Save' })}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ClinicPage;
